import type { LucideIcon } from "lucide-react"
import {
  FileArchive,
  FileAudio,
  FileCode2,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
} from "lucide-react"

export function formatFileSize(size: number): string {
  if (!Number.isFinite(size) || size <= 0) {
    return "0 B"
  }

  const units = ["B", "KB", "MB", "GB"]
  let value = size
  let unitIndex = 0

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }

  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`
}

type FileTypePresentation = {
  icon: LucideIcon
  label: string
  colorClassName: string
}

const FILE_TYPE_PRESENTATIONS: Record<string, FileTypePresentation> = {
  xlsx: {
    icon: FileSpreadsheet,
    label: "Excel",
    colorClassName: "text-emerald-600",
  },
  xls: {
    icon: FileSpreadsheet,
    label: "Excel",
    colorClassName: "text-emerald-600",
  },
  csv: {
    icon: FileSpreadsheet,
    label: "CSV",
    colorClassName: "text-emerald-600",
  },
  pdf: {
    icon: FileText,
    label: "PDF",
    colorClassName: "text-red-600",
  },
  doc: {
    icon: FileText,
    label: "Word",
    colorClassName: "text-blue-600",
  },
  docx: {
    icon: FileText,
    label: "Word",
    colorClassName: "text-blue-600",
  },
  txt: {
    icon: FileText,
    label: "TXT",
    colorClassName: "text-slate-600",
  },
  md: {
    icon: FileText,
    label: "Markdown",
    colorClassName: "text-slate-600",
  },
  jpg: {
    icon: FileImage,
    label: "JPG",
    colorClassName: "text-violet-600",
  },
  jpeg: {
    icon: FileImage,
    label: "JPG",
    colorClassName: "text-violet-600",
  },
  png: {
    icon: FileImage,
    label: "PNG",
    colorClassName: "text-violet-600",
  },
  gif: {
    icon: FileImage,
    label: "GIF",
    colorClassName: "text-violet-600",
  },
  webp: {
    icon: FileImage,
    label: "WEBP",
    colorClassName: "text-violet-600",
  },
  zip: {
    icon: FileArchive,
    label: "ZIP",
    colorClassName: "text-amber-600",
  },
  rar: {
    icon: FileArchive,
    label: "RAR",
    colorClassName: "text-amber-600",
  },
  "7z": {
    icon: FileArchive,
    label: "7Z",
    colorClassName: "text-amber-600",
  },
  mp3: {
    icon: FileAudio,
    label: "音频",
    colorClassName: "text-pink-600",
  },
  wav: {
    icon: FileAudio,
    label: "音频",
    colorClassName: "text-pink-600",
  },
  mp4: {
    icon: FileVideo,
    label: "视频",
    colorClassName: "text-cyan-600",
  },
  mov: {
    icon: FileVideo,
    label: "视频",
    colorClassName: "text-cyan-600",
  },
  avi: {
    icon: FileVideo,
    label: "视频",
    colorClassName: "text-cyan-600",
  },
  json: {
    icon: FileCode2,
    label: "JSON",
    colorClassName: "text-indigo-600",
  },
  ts: {
    icon: FileCode2,
    label: "代码",
    colorClassName: "text-indigo-600",
  },
  js: {
    icon: FileCode2,
    label: "代码",
    colorClassName: "text-indigo-600",
  },
}

export function getFileTypePresentation(
  extension?: string | null,
): FileTypePresentation & { suffix: string } {
  const normalizedExtension = extension?.replace(/^\./, "").toLowerCase() || ""
  const matchedPresentation = FILE_TYPE_PRESENTATIONS[normalizedExtension]

  if (matchedPresentation) {
    return {
      ...matchedPresentation,
      suffix: normalizedExtension.toUpperCase(),
    }
  }

  return {
    icon: FileText,
    label: normalizedExtension ? normalizedExtension.toUpperCase() : "文件",
    colorClassName: "text-slate-600",
    suffix: normalizedExtension ? normalizedExtension.toUpperCase() : "-",
  }
}
